/**
 * BYO v2 — Python package auto-detection.
 *
 * Pure, framework-free helpers that scan user Python source for `import` /
 * `from X import …` statements and map the imported top-level module to its
 * pip distribution name using a curated map (the common cases where the import
 * name and the pip package name differ). Anything not in the map falls through
 * to the import name itself.
 *
 * 100% unit-testable: no React, no Redux, no I/O.
 */

/**
 * Curated import-name → pip-package-name map. Covers the well-known cases where
 * the module you `import` is published under a different distribution name.
 * Extend as needed; unknown imports fall through to the import name.
 */
export const IMPORT_TO_PIP: Readonly<Record<string, string>> = Object.freeze({
  cv2: 'opencv-python',
  PIL: 'Pillow',
  sklearn: 'scikit-learn',
  bs4: 'beautifulsoup4',
  yaml: 'PyYAML',
  Crypto: 'pycryptodome',
  dateutil: 'python-dateutil',
  snowflake: 'snowflake-connector-python',
});

/**
 * Python standard-library modules we should NOT surface as pip packages.
 * Not exhaustive, but covers the modules that realistically show up in BYO
 * processor code so we don't suggest installing them.
 */
const STDLIB_MODULES = new Set<string>([
  'os',
  'sys',
  'json',
  're',
  'math',
  'random',
  'time',
  'datetime',
  'collections',
  'itertools',
  'functools',
  'typing',
  'io',
  'csv',
  'logging',
  'pathlib',
  'subprocess',
  'shutil',
  'tempfile',
  'glob',
  'hashlib',
  'base64',
  'uuid',
  'string',
  'decimal',
  'abc',
  'copy',
  'enum',
  'warnings',
  'traceback',
  'asyncio',
  'threading',
  'multiprocessing',
  'urllib',
  'http',
  'socket',
  'struct',
  'zlib',
  'gzip',
  'pickle',
  'sqlite3',
  'unittest',
  'argparse',
  'contextlib',
  'dataclasses',
  '__future__',
]);

/** Strip a `# comment` tail from a single line of Python source. */
function stripComment(line: string): string {
  const hashIdx = line.indexOf('#');
  return hashIdx === -1 ? line : line.slice(0, hashIdx);
}

/**
 * Extract the set of top-level imported module names from Python source.
 * Handles:
 *   - `import a`
 *   - `import a.b.c`
 *   - `import a as x, b.c as y`
 *   - `from a.b import c`
 * Relative imports (`from . import x`) are ignored.
 */
export function extractImportedModules(code: string): string[] {
  if (!code) return [];
  const modules = new Set<string>();

  for (const rawLine of code.split(/\r?\n/)) {
    const line = stripComment(rawLine).trim();
    if (!line) continue;

    // `from X import …` — only the top-level package of X matters.
    const fromMatch = /^from\s+(\.*)([A-Za-z0-9_.]*)\s+import\s+/.exec(line);
    if (fromMatch) {
      // Relative import (leading dots, e.g. `from . import x`) → skip.
      if (fromMatch[1]) continue;
      const top = fromMatch[2].split('.')[0];
      if (top) modules.add(top);
      continue;
    }

    // `import A`, `import A as x`, `import A, B.c as y`
    const importMatch = /^import\s+(.+)$/.exec(line);
    if (importMatch) {
      for (const part of importMatch[1].split(',')) {
        const name = part
          .trim()
          .split(/\s+as\s+/)[0]
          .trim();
        const top = name.split('.')[0];
        if (top) modules.add(top);
      }
    }
  }

  return Array.from(modules);
}

/** Map a single imported module name to its pip package name. */
export function importToPipName(moduleName: string): string {
  return IMPORT_TO_PIP[moduleName] ?? moduleName;
}

/**
 * Auto-detect the pip packages a piece of Python source depends on.
 *
 * - Parses imports, drops stdlib modules, maps via {@link IMPORT_TO_PIP}.
 * - Returns a clean, de-duplicated, alphabetically sorted list of bare pip
 *   package names (no version pins — the author can pin via the chips UI).
 */
export function detectPackages(code: string): string[] {
  const detected = new Set<string>();
  for (const mod of extractImportedModules(code)) {
    if (STDLIB_MODULES.has(mod)) continue;
    detected.add(importToPipName(mod));
  }
  return Array.from(detected).sort((a, b) => a.localeCompare(b));
}

/**
 * The bare package name from a possibly-pinned requirement string.
 * `pandas==2.2.0` → `pandas`; `opencv-python` → `opencv-python`.
 * Handles the common requirement operators (`==`, `>=`, `<=`, `~=`, `>`, `<`,
 * `!=`).
 */
export function bareName(requirement: string): string {
  return requirement.split(/[=<>!~]/)[0].trim();
}

/**
 * Merge freshly auto-detected packages into an existing (possibly pinned) list
 * without clobbering version pins the author already set.
 *
 * - Existing entries are kept verbatim (preserves pins + order).
 * - A detected package is appended only if no existing entry already covers it
 *   (matched by bare name).
 */
export function mergeDetectedPackages(existing: string[], detected: string[]): string[] {
  const existingBare = new Set(existing.map(bareName));
  const merged = [...existing];
  for (const pkg of detected) {
    if (!existingBare.has(bareName(pkg))) {
      merged.push(pkg);
      existingBare.add(bareName(pkg));
    }
  }
  return merged;
}
